import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
  SimpleChanges,
  OnChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { SpacerMixMethods, UnitType } from 'libs/constants';
import { Popover } from 'primeng/popover';
import { ApplicationStateService, ControlPointService2, MasterDataService } from 'libs/shared/services';
import { concat, Observable, of, combineLatest, Subscription } from 'rxjs';
import { map, shareReplay, switchMap, startWith } from 'rxjs/operators';
import { ControlPointState, ControlPointType, DesignCriteriaOption } from '../../../shared/constant';
import { ViewState } from '../../view-state';
import { PumpScheduleStateManager } from '../../state/schedule-state-manager';
import { IPumpScheduleFluidType, Job, PumpSchedule, PumpScheduleStageModel } from 'libs/models';
import { buildStagesFromProcedures } from '../../../control-point/components/job-procedure/domain/mappers/job-procedure-to-pump-schedule.map';
import { JobModalManagementService } from 'libs/ui/services/job-modal-management.service';
import { PumpScheduleStageStateManager } from '../../models/pump-schedule-stage-state-manager.model';
import { LinerComponent } from '../liner/liner.component';
import { ngrxCP1Store } from '../../../shared/ngrx-storage/ngrx-cp1-storage/ngrx-cp1-storage';
import { JobProcedure } from '../../../control-point/components/job-procedure/domain/models/job-procedure.model';
import { ShoeTrackData, ShoeTrackDataChange, DEFAULT_SHOE_TRACK_DATA } from '../../../control-point/components/cp-pump/cp-pump.store';

@Component({
    selector: 'shoetrack',
    templateUrl: './shoetrack.component.html',
    styleUrls: ['./shoetrack.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ShoeTrackComponent implements OnInit, OnDestroy, OnChanges {

    public readonly UnitType = UnitType;

    public readonly ControlPointState = ControlPointState;

    public readonly spacerMixMethods = SpacerMixMethods;

    public isJobTypeLinerUpdated = true; // updated from job edit screen

    cpStore = inject(ngrxCP1Store, { optional: true });

    @ViewChild(LinerComponent) LinerComponent: LinerComponent;
    @Input() public job: Job;

    @Input()
    public readonly scheduleState: PumpScheduleStateManager;

    @Input()
    public readonly listPumpSchedule$: Observable<PumpSchedule[]>;

    @Input()
    public namePumpSchedule: string;

    @Input()
    public isCmtSvc: boolean = true;

    @Input()
    public listPumpScheduleStageStateManager$: Observable<PumpScheduleStageStateManager[]>;

    @Input()
    public controlPointType: ControlPointType;

    @Input() submitStatus: boolean;

    @Input() completeStatus:boolean;

    @Input() jobProcedureTabName: string;

    @Input() jobProceduresActive: JobProcedure[] = [];

    @Input() jobProceduresUserInitiated = false;

    @Input() shoeTrackData: ShoeTrackData | null = null;

    @Output() shoeTrackDataChange = new EventEmitter<ShoeTrackDataChange>();

    private shoeTrackFormSubscription: Subscription | null = null;
    private suppressFormEmit = false;
    private _fluidTypes: IPumpScheduleFluidType[] = [];
    private readonly _masterDataService = inject(MasterDataService);

    public linerParamsVisibleChanged$: Observable<boolean>;

    public isLinerParamsRequired$: Observable<boolean>;
    public isLinerParamsVisible$: Observable<boolean>;

    public capturedLinerParamsRestrictions = undefined;
    public isLinerParamsRequired: boolean;
    public isLinerParamsVisible: boolean;
    designCriteriaOption = DesignCriteriaOption;

    private subscription: Subscription = new Subscription();

    constructor(

        private readonly _applicationStateService: ApplicationStateService,
        private jobModalManagementService: JobModalManagementService,
        private controlPointService: ControlPointService2,
    ) {}

  private get _viewState(): ViewState {
        return this.scheduleState.viewState;
    }

    public get pumpScheduleForm(): UntypedFormGroup {

        return this.scheduleState.form;
    }

    public get isScheduleEditView(): boolean {

        return this._viewState.isScheduleEditView;
    }

    public get isScheduleCp4View(): boolean {

        return this._viewState.isCP4View;
    }

    public get isJobTypeLiner(): boolean {

        if (this._viewState.isJobTypeLiner != undefined && this.isJobTypeLinerUpdated == undefined)
        {
            this.isJobTypeLinerUpdated = this._viewState.isJobTypeLiner;
        }
        return this._viewState.isJobTypeLiner;
    }

    public get isJobLiner(): Observable<boolean> {
        return this.linerParamsVisibleChanged$;
    }

    public get isScheduledShutdownRequired(): boolean {
      return this.isCmtSvc && this.isJobTypeLinerUpdated;
    }

    public get linerParamsVisible(): boolean {
        return this._viewState.isJobTypeLiner && (this._viewState.isScheduleEditView || this._viewState.isCP1View || this._viewState.isCP2View)
    }

    public get enablingCPStates$(): Observable<boolean> {

        return this._viewState.isCP1Submitted$
            .pipe(
                map(isCP1Submitted => {

                    return !this._viewState.isJobCompleted
                        && (isCP1Submitted || this._viewState.isCP2Approved)
                        && !this._viewState.isCP2Completed
                        && !this._viewState.isCP4Completed;
                }),
                shareReplay()
            );
    }

    public get isThickeningTimeDisable$(): Observable<boolean> {
        return combineLatest([
            this.controlPointService.controlPoint1State$,
            this.controlPointService.controlPoint2State$
        ]).pipe(
            map(([cp1State, cp2State]) => {
                const isCP1SubmittedOrApproved = cp1State === ControlPointState.Submitted
                    || cp1State === ControlPointState.Approved;

                const isCP2PreparedOrCompleted = cp2State === ControlPointState.Prepared
                    || cp2State === ControlPointState.Completed;

                if (this._viewState.isCP1View) {
                    return isCP1SubmittedOrApproved;
                }

                if (this._viewState.isCP2View) {
                    return isCP2PreparedOrCompleted;
                }

                if (this.isScheduleEditView) {
                    return isCP1SubmittedOrApproved && isCP2PreparedOrCompleted;
                }

                return false;
            })
        );
    }

    public ngOnInit(): void {

        this.subscription.add(
            this._masterDataService.listPumpScheduleFluidTypes().subscribe(types => {
                this._fluidTypes = types;
                this._syncFromProcedures();
            })
        );

        this.linerParamsVisibleChanged$ = concat(
            of(this.isJobTypeLiner),
            this._applicationStateService.jobTypeChanged
                .pipe(
                    map(jobType => {

                        this.isJobTypeLinerUpdated = jobType.context.isLiner; // update from job edit screen

                        return jobType.context.isLiner
                            && (this._viewState.isScheduleEditView || this._viewState.isCP1View || this._viewState.isCP2View);
                    })
                )
        );

        this.isLinerParamsRequired$ = combineLatest([
                this.listPumpScheduleStageStateManager$.pipe(map(list => list[0]?.scheduleState?.viewState?.isJobTypeLiner)),
                this._applicationStateService.jobEditOpened$.pipe(startWith(undefined)),
                this._applicationStateService.jobClassification$.pipe(startWith(undefined)),
                this._applicationStateService.jobTypeChanged.pipe(startWith(undefined))
            ]).pipe(
                 // eslint-disable-next-line
                switchMap((([isJobTypeLiner, _, jobClassificationState, jobType]) => {

                    if (this._viewState.controlPointNumber == 2) {
                        return of(false);
                    }

                    const isLiner = jobType?.context?.isLiner ?? isJobTypeLiner;
                    const isCmtSvc = jobClassificationState?.cmtSvc ?? this.isCmtSvc;
                    const isLinerParamsRequired = isLiner && isCmtSvc;
                    this.isLinerParamsRequired = this._applicationStateService.jobEditOpened$.value
                        ? isLinerParamsRequired
                        : this.capturedLinerParamsRestrictions?.isLinerParamsRequired ??(this.isCmtSvc && isJobTypeLiner);

                    return of(this.isLinerParamsRequired);
                }))
            );

        this.isLinerParamsVisible$ = combineLatest([
                this.listPumpScheduleStageStateManager$.pipe(map(list => list[0]?.scheduleState?.viewState?.isJobTypeLiner)),
                this._applicationStateService.jobEditOpened$.pipe(startWith(undefined)),
                this._applicationStateService.jobTypeChanged.pipe(startWith(undefined))
            ]).pipe(
          // eslint-disable-next-line
                switchMap((([isJobTypeLiner, _, jobType]) => {

                    const isLiner = jobType?.context?.isLiner ?? isJobTypeLiner;
                    this.isLinerParamsVisible = this._applicationStateService.jobEditOpened$.value
                        ? isLiner
                        : this.capturedLinerParamsRestrictions?.isLinerParamsVisible ?? isJobTypeLiner;

                    return of(this.isLinerParamsVisible);
                }))
            );

        this.subscription.add(
            this.jobModalManagementService.jobEditSaveButtonWasPushed$.subscribe(() => {
                this.capturedLinerParamsRestrictions = {
                    isLinerParamsVisible: this.isLinerParamsVisible,
                    isLinerParamsRequired: this.isLinerParamsRequired
                };
            })
        );

        this.subscription.add(
            this.jobModalManagementService.$saveError.subscribe(() => {
                this.capturedLinerParamsRestrictions = undefined;
            })
        );

        // this.jobModalManagementService.$cancelJob.subscribe(_ => {
        //     console.error('$cancelJob');
        //     this.isJobTypeLinerUpdated = this.isJobTypeLiner; // restore original jobtype when it is changed on edit job but not saved
        // });

        this.subscribeToShoeTrackFormChanges();
    }

    ngOnChanges(changes: SimpleChanges): void {
      if ((changes['shoeTrackData'] || changes['scheduleState']) && this.pumpScheduleForm) {
        if (this.shoeTrackData) {
          this.patchFormFromShoeTrackData(this.shoeTrackData);
        } else {
          this.patchFormFromShoeTrackData(DEFAULT_SHOE_TRACK_DATA);
        }
      }

      if (changes['jobProceduresActive'] && this.scheduleState) {
        this._syncFromProcedures();
      }
    }

    private _syncFromProcedures(): void {
      if (!this._fluidTypes?.length || !this.jobProceduresActive?.length) return;

      const pumpScheduleId = this.scheduleState.form?.get('pumpScheduleId')?.value || null;
      const stages: PumpScheduleStageModel[] = buildStagesFromProcedures(
        this.jobProceduresActive, pumpScheduleId, 0, this._fluidTypes
      );

      if (stages.length > 0) {
        this.scheduleState.syncStagesFromModel(stages, this.jobProceduresUserInitiated);
      }
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
        this.shoeTrackFormSubscription?.unsubscribe();
    }

    public onSelectedOverlayPanel(event, overlayPanelCurrent: Popover, overlayPanelDisplayed: Popover) {

        overlayPanelDisplayed.hide();
        overlayPanelCurrent.toggle(event);
    }

    public openTTDocument(event:Event){
        event.preventDefault();
        this.controlPointService.getTestResultDocumentUrl(this.designCriteriaOption.ThickeningTime).subscribe(result =>{
            window.open(result.url, '_blank');
        });
    }
    public getLinerFormData(): any {
        const value = this.LinerComponent?.getRawValue();
        return value;
    }

    public subscribeToShoeTrackFormChanges(): void {
      this.shoeTrackFormSubscription?.unsubscribe();
      if (!this.pumpScheduleForm) return;

      const controlNames = [
        'shoeTrackLength', 'shoeTrackVolume',
        'scheduledShutdown', 'targetSafetyFactor', 'batchMixingTime',
        'spacerMixMethod', 'isManuallySpacerMixMethod', 'linerCirculationMethod', 'linerCementLength'
      ];

      const controls = controlNames
        .map(name => ({ name, control: this.pumpScheduleForm.get(name) }))
        .filter(c => c.control != null);

      if (controls.length === 0) return;

      this.shoeTrackFormSubscription = combineLatest(
        controls.map(c => c.control!.valueChanges.pipe(startWith(c.control!.value)))
      ).pipe(
        map(values => {
          const result: Partial<ShoeTrackData> = {};
          controls.forEach((c, i) => {
            (result as any)[c.name] = values[i];
          });
          return result;
        })
      ).subscribe(data => {
        if (!this.suppressFormEmit) {
          const isUserInitiated = controls.some(item => item.control!.dirty);
          this.shoeTrackDataChange.emit({ data, isUserInitiated });
        }
      });
    }

    private patchFormFromShoeTrackData(data: ShoeTrackData): void {
      if (!this.pumpScheduleForm) return;

      this.suppressFormEmit = true;

      const fields: (keyof ShoeTrackData)[] = [
        'shoeTrackLength', 'shoeTrackVolume',
        'scheduledShutdown', 'targetSafetyFactor', 'batchMixingTime',
        'spacerMixMethod', 'linerCirculationMethod', 'linerCementLength'
      ];

      fields.forEach(field => {
        const control = this.pumpScheduleForm.get(field);
        if (control && control.value !== data[field]) {
          control.setValue(data[field], { emitEvent: false });
        }
      });

      // Re-subscribe so combineLatest picks up fresh startWith values
      // matching the patched form state. Called while suppressFormEmit is
      // still true so the initial startWith emission is suppressed.
      this.subscribeToShoeTrackFormChanges();

      this.suppressFormEmit = false;
    }
}
